/**
 * AssetRadar — German wording for permission-gated controls.
 *
 * Why this module exists: every page independently hand-wrote its own tooltip
 * for a control the user may not trigger ("Keine Berechtigung — erfordert
 * „scan.run“ …", "Keine Berechtigung: notification.send erforderlich", "Für
 * diese Aktion fehlt die Berechtigung user.admin.") — each with the permission
 * name typed as FREE TEXT and each phrased differently.
 *
 * That string is the one piece of UI copy that must match
 * app/auth/permissions.py exactly: a typo in it still compiles and still
 * renders, it just tells the operator to ask an administrator for a right that
 * does not exist. Every helper here takes a `Permission`, so the frozen
 * vocabulary is checked by the compiler, and the German phrasing is now
 * identical on the dashboard, the triage queue, the import and the two admin
 * screens.
 *
 * The "who holds it" part is DERIVED from the mirrored matrix rather than
 * written out, so it cannot claim "Rolle Administrator" for a permission a
 * future role also holds. `RolesMatrix` diffs that mirror against
 * `GET /api/admin/roles` and warns when the two drift apart.
 */

import {
  PERMISSION_DESCRIPTIONS,
  ROLES,
  ROLE_LABELS,
  ROLE_PERMISSIONS,
  type Permission,
  type Role,
} from './types';

/** Roles that grant `permission`, in the order of `ROLES`. */
export function rolesHolding(permission: Permission): readonly Role[] {
  return ROLES.filter((role) => ROLE_PERMISSIONS[role].includes(permission));
}

/** '(Rolle: Administrator)' / '(Rollen: Administrator, Auditor (nur lesen))' / ''. */
function holderSuffix(permission: Permission): string {
  const holders = rolesHolding(permission).map((role) => ROLE_LABELS[role]);
  if (holders.length === 0) return '';
  return ` (${holders.length === 1 ? 'Rolle' : 'Rollen'}: ${holders.join(', ')})`;
}

/**
 * Native tooltip for a control that is rendered but disabled because the
 * signed-in account lacks the permission its endpoint enforces.
 *
 * Disabled-and-explained beats hidden here: an auditor should be able to see
 * WHERE an administrator acts, and an administrator missing a single permission
 * gets the exact name to ask for.
 */
export function missingPermissionTitle(permission: Permission): string {
  return `Keine Berechtigung: ${PERMISSION_DESCRIPTIONS[permission]}${holderSuffix(permission)}.`;
}

/**
 * Sentence for a page-level read-only banner, naming every permission the page
 * would need for its mutating controls.
 */
export function readOnlyNotice(...permissions: readonly Permission[]): string {
  const names = permissions.map((permission) => `„${PERMISSION_DESCRIPTIONS[permission]}“`).join(' bzw. ');
  return `Nur-Lese-Zugriff: Sie sehen alle Daten dieser Seite. Zum Bearbeiten fehlt die Berechtigung ${names}.`;
}
